import React from 'react';
import axios from 'axios';

import auth from '../utils/auth';
import { formatCurrency } from '../utils';
import { loaders } from '../utils/loaders';
import cartUtils from '../utils/cart';

import { Link, useParams } from 'react-router-dom';
import FormattedNumber from '../components/FormattedNumber.jsx';
import Loading from '../components/Loading.jsx';
import Alert from 'react-s-alert';
import { useUser } from './User';
import { useSelector } from 'react-redux';
import { userPermissions } from './UserDashboard';

const deleteCart = async (userId, cartId) => {
    const { data } = await axios.request({
        url: `/customers/${userId}/carts/${cartId}`,
        method: 'DELETE',
        headers: {
            'Content-Type': 'application/json',
            'Session-Key': auth.getSID()
        }
    });
    return data;
};

const createUserCart = async (userId, options) => {
    const { data } = await axios.request({
        url: `/customers/${userId}/carts`,
        method: 'POST',
        headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
            'Session-Key': auth.getSID()
        },
        data: {
            ...options,
            items: []
        }
    });
    return data;
};

const CartTableRow = ({ cart, user, org, onDelete, isReadOnly }) => {
    let items = cart.items || [];
    let isMalformed = !Object.keys(cart).includes('items');
    return (
        <tr key={cart._id} className={`${isMalformed ? 'malformed' : ''}`}>
            <td>
                <Link to={`/customer/${user._id}/carts/${cart._id}`}>{cartUtils.getName(cart)}</Link>
            </td>
            <td>
                <FormattedNumber
                    value={formatCurrency(cartUtils.getGrandTotal(items, org.price_contracts, true))}
                    currency={org.currency}
                />{' '}
                ({cartUtils.getUnitCount(items)} units)
            </td>
            <td>
                {!isReadOnly && (
                    <button className="button button-small button-danger" onClick={(e) => onDelete(cart._id)}>
                        Delete
                    </button>
                )}
            </td>
        </tr>
    );
};

const UserCarts = () => {
    const params = useParams();
    const session = useSelector((state) => state.session);
    const { user, org, sop } = useUser(params.user_id);
    const [inProgress, setInProgress] = React.useState(true);
    const [carts, setCarts] = React.useState([]);
    let isReadOnly = (session?.permissions || []).includes(userPermissions.READONLY);

    React.useEffect(() => {
        (async () => {
            const resp = await loaders.user_carts.load(params.user_id);
            setInProgress(false);
            setCarts(resp);
        })();
    }, [params.user_id]);

    const handleDelete = async (cartId) => {
        try {
            await deleteCart(params.user_id, cartId);
            loaders.user_carts.clear(params.user_id);
            let resp = await loaders.user_carts.load(params.user_id);
            setInProgress(false);
            setCarts(resp);
        } catch {
            setInProgress(false);
            setCarts([]);
        }
    };

    const createCart = async (userId, options = {}) => {
        const prevCarts = [...carts];
        try {
            const data = await createUserCart(userId, options);
            loaders.user_carts.clear(userId);
            setCarts((carts) => [...carts, data]);
        } catch (error) {
            Alert.error(`Error creating cart.`);
            Alert.error(error.message);
            setCarts(prevCarts);
        }
    };

    const handleCreateCart = () => {
        if (!carts.find((c) => c.is_primary)) {
            createCart(user._id, { is_primary: true, is_pif: false });
        }

        if (user.pif_enabled && !carts.find((c) => c.is_pif)) {
            createCart(user._id, { is_primary: false, is_pif: true });
        }

        if (sop.length) {
            let usedSubs = carts.filter((c) => c.sop_id).map((c) => c.sop_id) || [];
            sop.forEach((sub) => {
                if (usedSubs.includes(sub.internal_id)) return false;
                createCart(user._id, {
                    sop_id: sub.internal_id,
                    is_primary: false,
                    is_pif: false
                });
            });
        }
    };

    const handleNewCart = () => {
        createCart(user._id, { is_primary: false, is_pif: false, is_wishlist: true });
    };

    return (
        <div>
            <h2>Carts & Wishlists</h2>
            <hr />

            {!isReadOnly && (
                <>
                    <button className="button" onClick={handleCreateCart}>
                        Create Default Carts
                    </button>
                    <button className="button" onClick={handleNewCart}>
                        Create New Cart
                    </button>
                </>
            )}

            {inProgress && <Loading />}
            {!inProgress && (
                <table>
                    <thead>
                        <tr>
                            <th>Name</th>
                            <th>Total</th>
                            <th></th>
                        </tr>
                    </thead>
                    <tbody>
                        {!carts.length && (
                            <tr>
                                <td colSpan={3}>No Carts</td>
                            </tr>
                        )}
                        {carts
                            .filter((cart) => cart.is_primary)
                            .map((cart) => (
                                <CartTableRow
                                    key={cart._id}
                                    cart={cart}
                                    user={user}
                                    org={org}
                                    onDelete={handleDelete}
                                    isReadOnly={isReadOnly}
                                />
                            ))}
                        {carts
                            .filter((cart) => cart.is_pif)
                            .map((cart) => (
                                <CartTableRow
                                    key={cart._id}
                                    cart={cart}
                                    user={user}
                                    org={org}
                                    onDelete={handleDelete}
                                    isReadOnly={isReadOnly}
                                />
                            ))}
                        {carts
                            .filter((cart) => cart.sop_id)
                            .map((cart) => (
                                <CartTableRow
                                    key={cart._id}
                                    cart={cart}
                                    user={user}
                                    org={org}
                                    onDelete={handleDelete}
                                    isReadOnly={isReadOnly}
                                />
                            ))}
                        {carts
                            .filter((cart) => !cart.is_primary && !cart.is_pif && !cart.sop_id)
                            .map((cart) => (
                                <CartTableRow
                                    key={cart._id}
                                    cart={cart}
                                    user={user}
                                    org={org}
                                    onDelete={handleDelete}
                                    isReadOnly={isReadOnly}
                                />
                            ))}
                    </tbody>
                </table>
            )}
        </div>
    );
};

export default UserCarts;
